import { ButtonBase, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { m } from 'framer-motion';
import Label from 'src/components/label';
import type { LabelColor } from 'src/components/label/types';

export type TableStatusFilterOption<Value extends string> = {
  value: Value;
  label: string;
  color: LabelColor;
};

type TableStatusFilterProps<Value extends string> = {
  options: readonly TableStatusFilterOption<Value>[];
  values: readonly Value[];
  onChange: (values: Value[]) => void;
  disabled?: boolean;
  singleSelection?: boolean;
  ariaLabelPrefix?: string;
};

export default function TableStatusFilter<Value extends string>({
  options,
  values,
  onChange,
  disabled = false,
  singleSelection = false,
  ariaLabelPrefix = 'Filtrar por status',
}: TableStatusFilterProps<Value>) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      flexWrap="nowrap"
      sx={{ flexShrink: 0 }}
    >
      {options.map((option) => {
        const isSelected = values.includes(option.value);
        const activeColor = option.color === 'default' ? 'grey.900' : `${option.color}.main`;
        const activeTextColor =
          option.color === 'default' || option.color === 'primary'
            ? 'common.white'
            : `${option.color}.contrastText`;

        const handleClick = () => {
          if (isSelected) {
            onChange(values.filter((currentValue) => currentValue !== option.value));
            return;
          }

          onChange(singleSelection ? [option.value] : [...values, option.value]);
        };

        return (
          <m.div
            key={option.value}
            animate={{ scale: isSelected ? 1.08 : 1 }}
            whileTap={disabled ? undefined : { scale: 0.98, y: 2 }}
            transition={{ type: 'spring', stiffness: 480, damping: 18, bounce: 0.35 }}
            style={{ display: 'inline-flex' }}
          >
            <ButtonBase
              disabled={disabled}
              aria-label={`${ariaLabelPrefix} ${option.label}`}
              aria-pressed={isSelected}
              onClick={handleClick}
              sx={{
                borderRadius: 1,
                overflow: 'visible',
                '&.Mui-focusVisible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Label
                variant="filled"
                color={option.color}
                sx={{
                  height: 26,
                  px: isSelected ? 1.25 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  border: '1px solid',
                  borderColor: isSelected ? activeColor : 'grey.900',
                  bgcolor: isSelected ? activeColor : 'common.white',
                  color: isSelected ? activeTextColor : 'grey.900',
                  boxShadow: (theme) => {
                    if (!isSelected) {
                      return `0 3px 0 ${alpha(theme.palette.grey[900], 0.28)}, 0 6px 10px ${alpha(
                        theme.palette.grey[900],
                        0.12
                      )}`;
                    }

                    let statusColor = theme.palette.grey[900];

                    if (option.color === 'primary') {
                      statusColor = theme.palette.primary.main;
                    } else if (option.color !== 'default') {
                      statusColor = theme.palette[option.color].dark;
                    }

                    return `0 4px 0 ${alpha(statusColor, 0.55)}, 0 7px 12px ${alpha(
                      statusColor,
                      0.2
                    )}`;
                  },
                  transition:
                    'padding 220ms ease, color 220ms ease, background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {option.label}
              </Label>
            </ButtonBase>
          </m.div>
        );
      })}
    </Stack>
  );
}
